import { apiFetch } from "@/lib/api";

export interface PaymentModalityData {
  monthly_cost: number;
  developer_count: number;
  project_months: number;
  is_custom: boolean;
  total_days: number;
  minimum_monthly_cost: number;
  saved_at?: string;
}

export interface CurrentMissionsData {
  total_cost: number;
  total_days: number;
  missions_count: number;
  selected_missions?: Array<{id: string; type: string; quantity: number}>;
  missions_details?: Array<any>;
  mission_titles?: string[];
}

export type MatchType = 'exact' | 'legacy_fallback' | 'none' | 'default';

export interface GetPaymentModalityResult {
  success: boolean;
  data?: PaymentModalityData;
  matchType?: MatchType;
  message?: string;
}

export interface MissionDetail {
  id: string;
  title: string;
  duration?: string;
  price?: number;
  description?: string;
  category?: string;
  quantity: number;
}

export interface OptionDetail {
  id: string;
  title: string;
  price?: number;
  duration?: string;
  description?: string;
  recurring?: boolean;
  quantity: number;
}

export interface Devis {
  id: string;
  devis_number: string;
  client_id: string;
  client_name: string;
  subtotal_ht: number;
  total_discounts: number;
  total_ht: number;
  total_ttc: number;
  estimated_duration: string;
  missions_count: number;
  options_count: number;
  selected_items: Array<{
    id: string;
    type: 'mission' | 'option';
    quantity: number;
  }>;
  pricing_details: {
    subtotal: number;
    total: number;
    discounts: Array<{name: string; amount: number}>;
    totalDiscounts: number;
  };
  missions_data?: Array<any>;
  options_data?: Array<any>;
  file_name: string;
  generated_at: string;
  created_at: string;
  updated_at: string;
  status?: 'non_confirme' | 'valide' | 'archive' | 'envoye_pour_signature';
  // Nouvelles colonnes détaillées
  missions_details?: MissionDetail[];
  options_details?: OptionDetail[];
  mission_titles?: string[];
  option_titles?: string[];
  total_duration_days?: number;
  payment_modality?: PaymentModalityData;
}

export interface DevisListResponse {
  success: boolean;
  data: Devis[];
  message?: string;
}

/**
 * Récupère la liste des devis pour un client donné
 */
export const getClientDevis = async (clientId: string): Promise<DevisListResponse> => {
  try {
    
    const result = await apiFetch(`/v1/devis/client/${clientId}`, {
      method: 'GET',
    });


    if (result && !result.error) {
      // Le backend retourne: { client_id, devis_count, devis: [...] }
      const devisList = result.devis || [];
      
      return {
        success: true,
        data: devisList
      };
    } else {
      console.warn('⚠️ Erreur dans la réponse API:', result);
      return {
        success: false,
        data: [],
        message: result?.detail || result?.message || 'Erreur lors de la récupération des devis'
      };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des devis:', error);
    
    // Log plus détaillé pour le debug
    if (error instanceof Error) {
      console.error('Détails de l\'erreur:', {
        message: error.message,
        stack: error.stack
      });
    }
    
    return {
      success: false,
      data: [],
      message: error instanceof Error ? error.message : 'Erreur de connexion avec le serveur'
    };
  }
};

/**
 * Formate la date au format français
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

/**
 * Formate le prix en euros
 */
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};

/**
 * Génère un statut basé sur les données du devis
 */
export const getDevisStatus = (devis: Devis): { label: string; variant: 'default' | 'secondary' | 'success' | 'destructive' } => {
  // Utiliser le statut de la base de données s'il existe
  if (devis.status) {
    switch (devis.status) {
      case 'non_confirme':
        return { label: 'Non confirmé', variant: 'destructive' };
      case 'valide':
        return { label: 'Validé', variant: 'success' };
      case 'archive':
        return { label: 'Archivé', variant: 'secondary' };
      case 'envoye_pour_signature':
        return { label: 'Envoyé pour signature', variant: 'default' };
      default:
        return { label: 'Non confirmé', variant: 'destructive' };
    }
  }
  
  // Fallback sur l'ancien système basé sur la date (pour les anciens devis)
  const now = new Date();
  const createdAt = new Date(devis.created_at);
  const daysDiff = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 1) {
    return { label: 'Nouveau', variant: 'success' };
  } else if (daysDiff <= 7) {
    return { label: 'Récent', variant: 'default' };
  } else {
    return { label: 'Ancien', variant: 'secondary' };
  }
};

/**
 * Supprime un devis (réservé à l'admin ac@ailix.ai)
 */
export const deleteDevis = async (devisId: string): Promise<{ success: boolean; message: string }> => {
  try {
    
    const result = await apiFetch(`/v1/devis/${devisId}`, {
      method: 'DELETE',
    });


    if (result && !result.error) {
      
      return {
        success: true,
        message: 'Devis supprimé avec succès'
      };
    } else {
      console.warn('⚠️ Erreur dans la réponse API:', result);
      return {
        success: false,
        message: result?.detail || result?.message || 'Erreur lors de la suppression du devis'
      };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la suppression du devis:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erreur de connexion avec le serveur'
    };
  }
};

/**
 * Change le statut d'un devis
 */
export const updateDevisStatus = async (devisId: string, status: 'non_confirme' | 'valide' | 'archive' | 'envoye_pour_signature'): Promise<{ success: boolean; message: string }> => {
  try {
    
    const result = await apiFetch(`/v1/devis/${devisId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    });


    if (result && !result.error) {
      
      return {
        success: true,
        message: 'Statut mis à jour avec succès'
      };
    } else {
      console.warn('⚠️ Erreur dans la réponse API:', result);
      return {
        success: false,
        message: result?.detail || result?.message || 'Erreur lors de la mise à jour du statut'
      };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la mise à jour du statut:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erreur de connexion avec le serveur'
    };
  }
};

// Cache simple pour éviter les appels redondants (TTL 5s, clé = clientId + currentMissionsData).
// Filet anti-flood : même si un consommateur ré-appelle en boucle, les requêtes identiques sont
// servies depuis le cache au lieu de frapper le réseau (cf. DEVIS-MODALITY-LOOP-001). Invalidé
// après chaque sauvegarde pour ne jamais resservir un état périmé.
const paymentModalityCache = new Map<string, { data: GetPaymentModalityResult; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 secondes
// Génération du cache : incrémentée à chaque sauvegarde. Un GET en vol qui résout APRÈS un save
// (donc avec une génération périmée) ne ré-écrit pas le cache → pas d'état pré-save resservi (race-safe).
let paymentModalityCacheGeneration = 0;

/**
 * Sauvegarde la modalité de paiement pour un devis
 */
export const savePaymentModality = async (
  clientId: string, 
  modalityData: PaymentModalityData,
  currentMissionsData?: CurrentMissionsData
): Promise<{ success: boolean; message: string; data?: PaymentModalityData }> => {
  try {
    
    const requestData = {
      ...modalityData,
      saved_at: new Date().toISOString(),
      current_missions: currentMissionsData || null
    };
    
    const result = await apiFetch(`/v1/devis/payment-modality/${clientId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });


    if (result && !result.error) {
      // Invalider le cache GET : la modalité vient de changer côté serveur, un GET ultérieur (même
      // dans la fenêtre TTL 5s) doit refléter l'état frais, pas l'ancien (cf. DEVIS-MODALITY-LOOP-001).
      // Incrémenter la génération AVANT le clear : neutralise aussi un GET déjà en vol (cf. getPaymentModality).
      paymentModalityCacheGeneration++;
      paymentModalityCache.clear();
      return {
        success: true,
        message: 'Configuration de l\'équipe sauvegardée avec succès',
        data: result.payment_modality
      };
    } else {
      console.warn('⚠️ Erreur dans la réponse API:', result);
      return {
        success: false,
        message: result?.detail || result?.message || 'Erreur lors de la sauvegarde de la modalité'
      };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde de la modalité:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erreur de connexion avec le serveur'
    };
  }
};

/**
 * Coercion défensive d'un champ numérique lu depuis une payment_modality (JSONB Supabase, non validé
 * runtime). Les écritures passent toujours par l'endpoint typé `float`, mais une donnée historique ou
 * écrite hors-endpoint (SQL manuel, import) pourrait stocker une string. Une virgule décimale FR
 * ("2,5") produirait NaN via les opérateurs du hook (`Math.round(x * 2)`, `Math.max(x, …)`), et le
 * drift-guard `NaN > 2` étant `false`, le NaN se propagerait jusqu'à l'état affiché/sauvegardé.
 * On normalise string→number (virgule FR comprise) ; toute valeur non finie retombe sur `fallback`.
 * Cf. DEVIS-MODALITY-DATATYPE-001 (pattern R7-CALLSITE-TYPES).
 */
const coerceNumericField = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

// Champs numériques normalisés à la lecture (cf. périmètre DEVIS-MODALITY-DATATYPE-001).
const NUMERIC_MODALITY_FIELDS = [
  'project_months',
  'developer_count',
  'monthly_cost',
  'minimum_monthly_cost',
] as const;

/**
 * Normalise les champs numériques d'une payment_modality lue depuis la DB en un POINT UNIQUE, pour que
 * tous les consommateurs (hook usePaymentModalityState) reçoivent des nombres finis. Préserve la forme :
 * un champ absent reste absent (pas d'injection de 0 dans un ancien enregistrement partiel).
 */
const sanitizePaymentModality = (
  modality: PaymentModalityData | null | undefined
): PaymentModalityData | null => {
  if (!modality || typeof modality !== 'object') {
    return modality ?? null;
  }
  const sanitized = { ...modality };
  for (const field of NUMERIC_MODALITY_FIELDS) {
    if (modality[field] !== undefined) {
      sanitized[field] = coerceNumericField(modality[field]);
    }
  }
  return sanitized;
};

/**
 * Récupère la modalité de paiement pour un client
 */
export const getPaymentModality = async (
  clientId: string,
  currentMissionsData?: CurrentMissionsData
): Promise<GetPaymentModalityResult> => {
  try {
    // Créer une clé de cache basée sur les paramètres
    const cacheKey = JSON.stringify({ clientId, currentMissionsData });
    const cached = paymentModalityCache.get(cacheKey);
    
    // Vérifier le cache
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // Capturer la génération AVANT le fetch : si un save survient pendant la requête en vol, la
    // génération changera et on NE remettra PAS en cache cette réponse (potentiellement pré-save).
    const generationAtStart = paymentModalityCacheGeneration;

    // Construire l'URL avec les paramètres de requête pour les missions actuelles
    let url = `/v1/devis/payment-modality/${clientId}`;
    const params = new URLSearchParams();

    if (currentMissionsData) {
      if (currentMissionsData.total_cost !== undefined) {
        params.append('total_cost', currentMissionsData.total_cost.toString());
      }
      if (currentMissionsData.total_days !== undefined) {
        params.append('total_days', currentMissionsData.total_days.toString());
      }
      if (currentMissionsData.missions_count !== undefined) {
        params.append('missions_count', currentMissionsData.missions_count.toString());
      }
      // Envoyer les IDs de missions pour le matching exact par scope_signature
      const missionIds = (currentMissionsData.selected_missions || [])
        .map(m => m.id)
        .filter(Boolean);
      if (missionIds.length > 0) {
        params.append('mission_ids', missionIds.join(','));
      }
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const result = await apiFetch(url, {
      method: 'GET',
    });

    if (result && !result.error) {
      const matchType = result.match_type || 'default';

      const response = {
        success: true,
        // Coercion défensive des champs numériques du JSONB (cf. sanitizePaymentModality).
        data: sanitizePaymentModality(result.payment_modality),
        matchType,
      };

      // Mettre en cache le résultat (sauf si un save a eu lieu pendant la requête en vol)
      if (generationAtStart === paymentModalityCacheGeneration) {
        paymentModalityCache.set(cacheKey, {
          data: response,
          timestamp: Date.now()
        });
      }

      return response;
    } else {
      // Si pas de modalité trouvée, ce n'est pas une erreur
      if (result?.detail?.includes('not found') || result?.message?.includes('not found')) {
        const response = {
          success: true,
          data: undefined,
          matchType: 'none' as const,
          message: 'Aucune modalité de paiement sauvegardée'
        };

        // Mettre en cache même les résultats vides (sauf si un save a eu lieu pendant la requête en vol)
        if (generationAtStart === paymentModalityCacheGeneration) {
          paymentModalityCache.set(cacheKey, {
            data: response,
            timestamp: Date.now()
          });
        }

        return response;
      }

      console.warn('Erreur dans la réponse API:', result);
      return {
        success: false,
        message: result?.detail || result?.message || 'Erreur lors de la récupération de la modalité'
      };
    }
    
  } catch (error) {
    console.error('❌ Erreur lors de la récupération de la modalité:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erreur de connexion avec le serveur'
    };
  }
};